/**
 * coords - Lat/Lon-Beschaffung für den Sonnenstand (#604)
 *
 * Dreistufig, damit Bestandsgärten ohne erneutes Setup funktionieren:
 *   1. settings.latitude/longitude (seit #604 beim PLZ-Lookup additiv gespeichert)
 *   2. Runtime-Lookup über die gespeicherte PLZ (erster Treffer; Ergebnis wird
 *      vom Aufrufer in die Settings nachgetragen → Selbstheilung)
 *   3. DE-Zentroid als letzter Fallback + Hinweis „Standort ungenau"
 *
 * Reines Modul ohne Context-/API-Bindung: die PLZ-Lookup-Funktion wird
 * injiziert, das Zurückschreiben erledigt der konsumierende Hook.
 */

/** Geografische Mitte Deutschlands (nahe Niederdorla) — ruhiger Default. */
export const DE_CENTROID = { latitude: 51.0, longitude: 10.3 };

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Koordinaten direkt aus den Settings, falls vorhanden und plausibel.
 * @param {object|null|undefined} settings
 * @returns {{latitude: number, longitude: number, source: 'settings'}|null}
 */
export function coordsFromSettings(settings) {
  const lat = settings?.latitude;
  const lon = settings?.longitude;
  if (isFiniteNumber(lat) && isFiniteNumber(lon)) {
    return { latitude: lat, longitude: lon, source: 'settings' };
  }
  return null;
}

/**
 * Auflösung der Koordinaten über die dreistufige Fallback-Kette.
 *
 * @param {object|null} settings - Garden-Settings (latitude/longitude/zipCode)
 * @param {object} [options]
 * @param {(zip: string) => Promise<{results?: Array<{lat?: number, lon?: number}>}>} [options.lookupPostalCode]
 *   Injizierte PLZ-Lookup-Funktion (z. B. postalCodesApi.lookup)
 * @returns {Promise<{latitude: number, longitude: number, source: 'settings'|'lookup'|'fallback'}>}
 */
export async function resolveCoordinates(settings, { lookupPostalCode } = {}) {
  const fromSettings = coordsFromSettings(settings);
  if (fromSettings) return fromSettings;

  const zip = settings?.zipCode;
  if (zip && typeof lookupPostalCode === 'function') {
    try {
      const data = await lookupPostalCode(zip);
      const hit = data?.results?.find(
        (r) => isFiniteNumber(r?.lat) && isFiniteNumber(r?.lon)
      );
      if (hit) {
        return { latitude: hit.lat, longitude: hit.lon, source: 'lookup' };
      }
    } catch {
      // Netz-/Lookup-Fehler → stiller Fallback auf Zentroid
    }
  }

  return { ...DE_CENTROID, source: 'fallback' };
}
